import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  ClassSeries,
  GroupClassInstance,
  ClassEnrollment,
  Service,
  Calendar,
  Client,
  Dog,
  CreateClassSeriesInput,
  CreateGroupClassInstanceInput,
  CreateClassEnrollmentInput,
} from "../lib/api";

// Helper to format time
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Status badges
function SeriesStatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
      Inactive
    </span>
  );
}

function InstanceStatusBadge({
  status,
}: {
  status: GroupClassInstance["status"];
}) {
  const styles: Record<GroupClassInstance["status"], string> = {
    scheduled: "bg-blue-100 text-blue-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-gray-100 text-gray-500",
  };

  const labels: Record<GroupClassInstance["status"], string> = {
    scheduled: "Scheduled",
    in_progress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function EnrollmentStatusBadge({ status }: { status: ClassEnrollment["status"] }) {
  const styles: Record<ClassEnrollment["status"], string> = {
    enrolled: "bg-blue-100 text-blue-700",
    attended: "bg-green-100 text-green-700",
    no_show: "bg-red-100 text-red-700",
    cancelled: "bg-gray-100 text-gray-500",
  };

  const labels: Record<ClassEnrollment["status"], string> = {
    enrolled: "Enrolled",
    attended: "Attended",
    no_show: "No Show",
    cancelled: "Cancelled",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

// Create Series Modal
interface CreateSeriesModalProps {
  services: Service[];
  calendars: Calendar[];
  onClose: () => void;
  onSuccess: () => void;
}

function CreateSeriesModal({
  services,
  calendars,
  onClose,
  onSuccess,
}: CreateSeriesModalProps) {
  const [name, setName] = useState("");
  const [appointmentTypeId, setAppointmentTypeId] = useState("");
  const [calendarId, setCalendarId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalSessions, setTotalSessions] = useState("");
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  const groupClassServices = services.filter((s) => s.isGroupClass && s.isActive);

  const createMutation = useMutation({
    mutationFn: (data: CreateClassSeriesInput) => api.classSeries.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create series");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim() || !appointmentTypeId || !calendarId || !startDate) {
      setError("Name, service, calendar, and start date are required");
      return;
    }

    createMutation.mutate({
      name: name.trim(),
      appointmentTypeId,
      calendarId,
      startDate,
      endDate: endDate || null,
      totalSessions: totalSessions ? parseInt(totalSessions, 10) : null,
      price: price ? parseFloat(price).toFixed(2) : null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Create Class Series</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="seriesName"
              className="block text-sm font-medium text-gray-700"
            >
              Series Name
            </label>
            <input
              id="seriesName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="serviceSelect"
              className="block text-sm font-medium text-gray-700"
            >
              Service
            </label>
            <select
              id="serviceSelect"
              value={appointmentTypeId}
              onChange={(e) => setAppointmentTypeId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            >
              <option value="">Select a group class service...</option>
              {groupClassServices.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="calendarSelect"
              className="block text-sm font-medium text-gray-700"
            >
              Calendar
            </label>
            <select
              id="calendarSelect"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            >
              <option value="">Select a calendar...</option>
              {calendars.map((calendar) => (
                <option key={calendar.id} value={calendar.id}>
                  {calendar.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700"
              >
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700"
              >
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="totalSessions"
                className="block text-sm font-medium text-gray-700"
              >
                Total Sessions
              </label>
              <input
                id="totalSessions"
                type="number"
                min="1"
                value={totalSessions}
                onChange={(e) => setTotalSessions(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
            <div>
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Price ($)
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? "Creating..." : "Create Series"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Instance Modal
interface CreateInstanceModalProps {
  series: ClassSeries;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateInstanceModal({
  series,
  onClose,
  onSuccess,
}: CreateInstanceModalProps) {
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [maxParticipants, setMaxParticipants] = useState("10");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateGroupClassInstanceInput) =>
      api.groupClassInstances.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create instance");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!date || !startTime || !endTime) {
      setError("Date and times are required");
      return;
    }

    const startDateTime = new Date(`${date}T${startTime}:00`).toISOString();
    const endDateTime = new Date(`${date}T${endTime}:00`).toISOString();

    createMutation.mutate({
      appointmentTypeId: series.appointmentTypeId,
      classSeriesId: series.id,
      calendarId: series.calendarId,
      startTime: startDateTime,
      endTime: endDateTime,
      maxParticipants: parseInt(maxParticipants, 10),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Add Class Instance</h2>
        <p className="text-sm text-gray-500 mb-4">
          Adding instance to: {series.name}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="instanceDate"
              className="block text-sm font-medium text-gray-700"
            >
              Date
            </label>
            <input
              id="instanceDate"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="instanceStartTime"
                className="block text-sm font-medium text-gray-700"
              >
                Start Time
              </label>
              <input
                id="instanceStartTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="instanceEndTime"
                className="block text-sm font-medium text-gray-700"
              >
                End Time
              </label>
              <input
                id="instanceEndTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="maxParticipants"
              className="block text-sm font-medium text-gray-700"
            >
              Max Participants
            </label>
            <input
              id="maxParticipants"
              type="number"
              min="1"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? "Adding..." : "Add Instance"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Enrollment Modal
interface AddEnrollmentModalProps {
  instance: GroupClassInstance;
  clients: Client[];
  onClose: () => void;
  onSuccess: () => void;
}

function AddEnrollmentModal({
  instance,
  clients,
  onClose,
  onSuccess,
}: AddEnrollmentModalProps) {
  const [clientId, setClientId] = useState("");
  const [dogId, setDogId] = useState("");
  const [error, setError] = useState("");

  // Fetch dogs for selected client
  const { data: dogsData } = useQuery({
    queryKey: ["dogs", "byClient", clientId],
    queryFn: () => api.dogs.byClient(clientId),
    enabled: !!clientId,
  });

  const dogs = dogsData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (data: CreateClassEnrollmentInput) =>
      api.classEnrollments.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to add enrollment");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!clientId) {
      setError("Please select a client");
      return;
    }

    createMutation.mutate({
      classInstanceId: instance.id,
      clientId,
      dogId: dogId || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Add Enrollment</h2>
        <p className="text-sm text-gray-500 mb-4">
          Class on {formatDateTime(instance.startTime)}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="clientSelect"
              className="block text-sm font-medium text-gray-700"
            >
              Client
            </label>
            <select
              id="clientSelect"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setDogId("");
              }}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            >
              <option value="">Select a client...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.firstName} {client.lastName}
                </option>
              ))}
            </select>
          </div>

          {clientId && dogs.length > 0 && (
            <div>
              <label
                htmlFor="dogSelect"
                className="block text-sm font-medium text-gray-700"
              >
                Dog (optional)
              </label>
              <select
                id="dogSelect"
                value={dogId}
                onChange={(e) => setDogId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              >
                <option value="">No specific dog</option>
                {dogs.map((dog: Dog) => (
                  <option key={dog.id} value={dog.id}>
                    {dog.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? "Adding..." : "Add Enrollment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Page Component
export function GroupClassesPage() {
  const queryClient = useQueryClient();
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(
    null
  );
  const [showCreateSeriesModal, setShowCreateSeriesModal] = useState(false);
  const [showCreateInstanceModal, setShowCreateInstanceModal] = useState(false);
  const [showAddEnrollmentModal, setShowAddEnrollmentModal] = useState(false);

  // Fetch class series
  const { data: seriesData, isLoading: isLoadingSeries } = useQuery({
    queryKey: ["classSeries"],
    queryFn: () => api.classSeries.list({ limit: 100 }),
  });

  // Fetch instances for selected series
  const { data: instancesData, isLoading: isLoadingInstances } = useQuery({
    queryKey: ["groupClassInstances", selectedSeriesId],
    queryFn: () =>
      api.groupClassInstances.list({
        classSeriesId: selectedSeriesId ?? undefined,
        limit: 100,
      }),
    enabled: !!selectedSeriesId,
  });

  // Fetch enrollments for selected instance
  const { data: enrollmentsData, isLoading: isLoadingEnrollments } = useQuery({
    queryKey: ["classEnrollments", selectedInstanceId],
    queryFn: () =>
      api.classEnrollments.list({
        classInstanceId: selectedInstanceId ?? undefined,
        limit: 100,
      }),
    enabled: !!selectedInstanceId,
  });

  // Fetch services for modal
  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.services.list({ limit: 100 }),
  });

  // Fetch calendars for modal
  const { data: calendarsData } = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.calendars.list({ limit: 100 }),
  });

  // Fetch clients for enrollment modal
  const { data: clientsData } = useQuery({
    queryKey: ["clients"],
    queryFn: () => api.clients.list({ limit: 100 }),
  });

  // Delete series mutation
  const deleteSeriesMutation = useMutation({
    mutationFn: (id: string) => api.classSeries.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classSeries"] });
      if (selectedSeriesId) {
        setSelectedSeriesId(null);
        setSelectedInstanceId(null);
      }
    },
  });

  // Cancel instance mutation
  const cancelInstanceMutation = useMutation({
    mutationFn: (id: string) =>
      api.groupClassInstances.update(id, { status: "cancelled" }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["groupClassInstances", selectedSeriesId],
      });
    },
  });

  // Mark attendance mutation
  const markAttendanceMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "attended" | "no_show";
    }) => api.classEnrollments.markAttendance(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classEnrollments", selectedInstanceId],
      });
    },
  });

  // Cancel enrollment mutation
  const cancelEnrollmentMutation = useMutation({
    mutationFn: (id: string) => api.classEnrollments.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classEnrollments", selectedInstanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["groupClassInstances", selectedSeriesId],
      });
    },
  });

  const series = seriesData?.data ?? [];
  const instances = instancesData?.data ?? [];
  const enrollments = enrollmentsData?.data ?? [];
  const services = servicesData?.data ?? [];
  const calendars = calendarsData?.data ?? [];
  const clients = clientsData?.data ?? [];

  const serviceMap = new Map(services.map((s) => [s.id, s]));
  const clientMap = new Map(clients.map((c) => [c.id, c]));

  const selectedSeries = series.find((s) => s.id === selectedSeriesId);
  const selectedInstance = instances.find((i) => i.id === selectedInstanceId);

  const handleSeriesSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["classSeries"] });
  };

  const handleInstanceSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["groupClassInstances", selectedSeriesId],
    });
  };

  const handleEnrollmentSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["classEnrollments", selectedInstanceId],
    });
    queryClient.invalidateQueries({
      queryKey: ["groupClassInstances", selectedSeriesId],
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Group Classes</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Series List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Class Series</h2>
            <button
              onClick={() => setShowCreateSeriesModal(true)}
              className="px-3 py-1 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
            >
              + New
            </button>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {isLoadingSeries ? (
              <div className="p-4 text-center text-gray-500 animate-pulse">
                Loading...
              </div>
            ) : series.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No class series yet.
              </div>
            ) : (
              series.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSelectedSeriesId(s.id);
                    setSelectedInstanceId(null);
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${
                    selectedSeriesId === s.id ? "bg-amber-50" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{s.name}</span>
                    <SeriesStatusBadge isActive={s.isActive} />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {serviceMap.get(s.appointmentTypeId)?.name ?? "Unknown Service"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Starts: {new Date(s.startDate).toLocaleDateString()}
                    {s.totalSessions && ` | ${s.totalSessions} sessions`}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Instances List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Class Instances
              {selectedSeries && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({selectedSeries.name})
                </span>
              )}
            </h2>
            {selectedSeries && (
              <button
                onClick={() => setShowCreateInstanceModal(true)}
                className="px-3 py-1 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
              >
                + Add
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {!selectedSeriesId ? (
              <div className="p-4 text-center text-gray-500">
                Select a series to view instances.
              </div>
            ) : isLoadingInstances ? (
              <div className="p-4 text-center text-gray-500 animate-pulse">
                Loading...
              </div>
            ) : instances.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No instances yet.
              </div>
            ) : (
              instances.map((instance) => (
                <div
                  key={instance.id}
                  onClick={() => setSelectedInstanceId(instance.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setSelectedInstanceId(instance.id);
                    }
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 cursor-pointer ${
                    selectedInstanceId === instance.id ? "bg-amber-50" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">
                      {formatDateTime(instance.startTime)}
                    </span>
                    <InstanceStatusBadge status={instance.status} />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatTime(instance.startTime)} -{" "}
                    {formatTime(instance.endTime)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {instance.currentEnrollments}/{instance.maxParticipants}{" "}
                    enrolled
                  </p>
                  {instance.status === "scheduled" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelInstanceMutation.mutate(instance.id);
                      }}
                      className="mt-2 text-xs text-red-600 hover:text-red-700"
                    >
                      Cancel Instance
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Enrollments List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Enrollments</h2>
            {selectedInstance &&
              selectedInstance.status === "scheduled" &&
              selectedInstance.currentEnrollments <
                selectedInstance.maxParticipants && (
                <button
                  onClick={() => setShowAddEnrollmentModal(true)}
                  className="px-3 py-1 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
                >
                  + Add
                </button>
              )}
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {!selectedInstanceId ? (
              <div className="p-4 text-center text-gray-500">
                Select an instance to view enrollments.
              </div>
            ) : isLoadingEnrollments ? (
              <div className="p-4 text-center text-gray-500 animate-pulse">
                Loading...
              </div>
            ) : enrollments.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No enrollments yet.
              </div>
            ) : (
              enrollments.map((enrollment) => {
                const client = clientMap.get(enrollment.clientId);
                return (
                  <div key={enrollment.id} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {client
                          ? `${client.firstName} ${client.lastName}`
                          : "Unknown Client"}
                      </span>
                      <EnrollmentStatusBadge status={enrollment.status} />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Enrolled:{" "}
                      {new Date(enrollment.enrolledAt).toLocaleDateString()}
                    </p>
                    {enrollment.status === "enrolled" && (
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() =>
                            markAttendanceMutation.mutate({
                              id: enrollment.id,
                              status: "attended",
                            })
                          }
                          disabled={markAttendanceMutation.isPending}
                          className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
                        >
                          Mark Attended
                        </button>
                        <button
                          onClick={() =>
                            markAttendanceMutation.mutate({
                              id: enrollment.id,
                              status: "no_show",
                            })
                          }
                          disabled={markAttendanceMutation.isPending}
                          className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
                        >
                          Mark No-Show
                        </button>
                        <button
                          onClick={() =>
                            cancelEnrollmentMutation.mutate(enrollment.id)
                          }
                          disabled={cancelEnrollmentMutation.isPending}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Selected Series Actions */}
      {selectedSeries && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => deleteSeriesMutation.mutate(selectedSeries.id)}
            disabled={deleteSeriesMutation.isPending}
            className="px-3 py-1 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            {deleteSeriesMutation.isPending ? "Deleting..." : "Delete Series"}
          </button>
        </div>
      )}

      {/* Modals */}
      {showCreateSeriesModal && (
        <CreateSeriesModal
          services={services}
          calendars={calendars}
          onClose={() => setShowCreateSeriesModal(false)}
          onSuccess={handleSeriesSuccess}
        />
      )}

      {showCreateInstanceModal && selectedSeries && (
        <CreateInstanceModal
          series={selectedSeries}
          onClose={() => setShowCreateInstanceModal(false)}
          onSuccess={handleInstanceSuccess}
        />
      )}

      {showAddEnrollmentModal && selectedInstance && (
        <AddEnrollmentModal
          instance={selectedInstance}
          clients={clients}
          onClose={() => setShowAddEnrollmentModal(false)}
          onSuccess={handleEnrollmentSuccess}
        />
      )}
    </div>
  );
}
